<template>
  <v-card class="cumplimiento-card" elevation="0">
    <div class="cumplimiento-header">
      <div class="cumplimiento-filters">
        <v-select
          v-model="filters.year"
          :items="yearOptions"
          label="Año"
          item-text="text"
          item-value="value"
          class="cumplimiento-filter"
          hide-details
        />
        <v-select
          v-model="filters.month"
          :items="monthOptions"
          label="Mes"
          item-text="text"
          item-value="value"
          class="cumplimiento-filter"
          hide-details
        />
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading"
          @click="fetchReport"
        >
          Buscar
        </v-btn>
        <v-btn
          color="success"
          :loading="downloadingExcel"
          :disabled="downloadingExcel"
          @click="downloadMonthlyOperatorMachineEfficiencyExcel"
        >
          Descargar Excel
        </v-btn>
      </div>

      <div class="cumplimiento-title-block">
        <div class="cumplimiento-title">
          {{ reportTitle }}
        </div>
        <div class="cumplimiento-subtitle">
          Porcentaje mensual general MITCEN: {{ formatPercent(summary.porcentajeMensualGeneralMitcen) }}
        </div>
      </div>
    </div>

    <div class="cumplimiento-body">
      <v-alert
        v-if="errorMessage"
        type="error"
        dense
        outlined
        class="mb-4"
      >
        {{ errorMessage }}
      </v-alert>

      <div class="cumplimiento-summary">
        <div class="summary-card">
          <div class="summary-label">Promedio mensual general</div>
          <div class="summary-value">
            {{ formatPercent(summary.porcentajeMensualGeneralMitcen) }}
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Registros</div>
          <div class="summary-value">
            {{ rows.length }}
          </div>
        </div>
      </div>

      <v-card class="cumplimiento-panel" outlined>
        <div class="panel-title">
          Detalle por operador y máquina
        </div>
        <v-data-table
          :headers="tableHeaders"
          :items="rows"
          :loading="loading"
          loading-text="Cargando cumplimiento..."
          class="elevation-0 cumplimiento-table"
          dense
          fixed-header
          height="460"
          :items-per-page="25"
        >
          <template v-slot:[`item.semana01`]="{ item }">
            {{ formatNumber(item.semana01) }}
          </template>
          <template v-slot:[`item.semana02`]="{ item }">
            {{ formatNumber(item.semana02) }}
          </template>
          <template v-slot:[`item.semana03`]="{ item }">
            {{ formatNumber(item.semana03) }}
          </template>
          <template v-slot:[`item.semana04`]="{ item }">
            {{ formatNumber(item.semana04) }}
          </template>
          <template v-slot:[`item.semana05`]="{ item }">
            {{ formatNumber(item.semana05) }}
          </template>
          <template v-slot:[`item.sumaTotalMensual`]="{ item }">
            {{ formatNumber(item.sumaTotalMensual) }}
          </template>
          <template v-slot:[`item.porcentajeMensual`]="{ item }">
            {{ formatPercent(item.porcentajeMensual) }}
          </template>
          <template v-slot:[`item.porcentajeParoMensual`]="{ item }">
            {{ formatPercent(item.porcentajeParoMensual) }}
          </template>
          <template v-slot:[`item.totalMensual`]="{ item }">
            {{ formatPercent(item.totalMensual) }}
          </template>
        </v-data-table>
      </v-card>

      <v-card class="cumplimiento-panel" outlined>
        <div class="panel-title">
          {{ chartTitle }}
        </div>
        <div v-if="hasChartData" class="chart-wrap">
          <apexchart
            ref="generalChart"
            type="bar"
            :height="chartHeight"
            :options="chartOptions"
            :series="chartSeries"
          />
        </div>
        <div v-else class="empty-state">
          No hay datos para mostrar en la gráfica.
        </div>
      </v-card>

      <v-card class="cumplimiento-panel" outlined>
        <div class="panel-title">
          PORCENTAJE DESGLOSE SEMANAL
        </div>
        <div v-if="hasWeeklyBreakdownData" class="chart-wrap">
          <apexchart
            ref="weeklyChart"
            type="bar"
            :height="weeklyChartHeight"
            :options="weeklyChartOptions"
            :series="weeklyChartSeries"
          />
        </div>
        <div v-else class="empty-state">
          No hay datos semanales para mostrar en la gráfica.
        </div>
      </v-card>

      <v-card class="cumplimiento-panel" outlined>
        <div class="panel-title">
          PORCENTAJE PARO MENSUAL POR OPERADOR/MÁQUINA
        </div>
        <div v-if="hasStopChartData" class="chart-wrap">
          <apexchart
            ref="stopChart"
            type="bar"
            :height="stopChartHeight"
            :options="stopChartOptions"
            :series="stopChartSeries"
          />
        </div>
        <div v-else class="empty-state">
          No hay porcentaje de paro mensual para mostrar en la gráfica.
        </div>
      </v-card>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";

const MONTH_OPTIONS = [
  { text: "Enero", value: 1 },
  { text: "Febrero", value: 2 },
  { text: "Marzo", value: 3 },
  { text: "Abril", value: 4 },
  { text: "Mayo", value: 5 },
  { text: "Junio", value: 6 },
  { text: "Julio", value: 7 },
  { text: "Agosto", value: 8 },
  { text: "Septiembre", value: 9 },
  { text: "Octubre", value: 10 },
  { text: "Noviembre", value: 11 },
  { text: "Diciembre", value: 12 },
];

function buildYearOptions() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear - 2; year <= currentYear + 2; year += 1) {
    years.push({ text: `${year}`, value: year });
  }
  return years;
}

function emptyPayload() {
  const now = new Date();
  return {
    periodo: {
      año: now.getFullYear(),
      mes: now.getMonth() + 1,
      mesNombre: MONTH_OPTIONS[now.getMonth()].text,
      titulo: "Cumplimiento del Plan de Producción",
    },
    filas: [],
    resumen: {
      porcentajeMensualGeneralMitcen: 0,
    },
  };
}

function emptyChartPayload() {
  const now = new Date();
  return {
    año: now.getFullYear(),
    mes: now.getMonth() + 1,
    mesNombre: MONTH_OPTIONS[now.getMonth()].text,
    titulo: "Cumplimiento del Plan de Producción",
    categorias: [],
    series: [],
    porcentajeMensualGeneralMitcen: 0,
  };
}

export default {
  name: "ReporteCumplimiento",
  data() {
    const now = new Date();
    return {
      loading: false,
      downloadingExcel: false,
      errorMessage: "",
      filters: {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      },
      yearOptions: buildYearOptions(),
      monthOptions: MONTH_OPTIONS,
      tablePayload: emptyPayload(),
      chartPayload: emptyChartPayload(),
      tableHeaders: [
        { text: "Operador", value: "operador", sortable: true, width: 220 },
        { text: "Máquina", value: "maquina", sortable: true, width: 220 },
        { text: "Operador / Máquina", value: "operadorMaquina", sortable: true, width: 280 },
        { text: "Semana 1", value: "semana01", sortable: true, align: "center" },
        { text: "Semana 2", value: "semana02", sortable: true, align: "center" },
        { text: "Semana 3", value: "semana03", sortable: true, align: "center" },
        { text: "Semana 4", value: "semana04", sortable: true, align: "center" },
        { text: "Semana 5", value: "semana05", sortable: true, align: "center" },
        { text: "Suma Total Mensual", value: "sumaTotalMensual", sortable: true, align: "center" },
        { text: "Porcentaje Mensual", value: "porcentajeMensual", sortable: true, align: "center" },
        { text: "Porcentaje Paro Mensual", value: "porcentajeParoMensual", sortable: true, align: "center" },
        { text: "Total Mensual", value: "totalMensual", sortable: true, align: "center" },
      ],
    };
  },
  computed: {
    rows() {
      return Array.isArray(this.tablePayload.filas) ? this.tablePayload.filas : [];
    },
    summary() {
      const tableValue = this.tablePayload
        && this.tablePayload.resumen
        && this.tablePayload.resumen.porcentajeMensualGeneralMitcen != null
        && this.tablePayload.resumen.porcentajeMensualGeneralMitcen !== ""
        && Number.isFinite(Number(this.tablePayload.resumen.porcentajeMensualGeneralMitcen))
        ? Number(this.tablePayload.resumen.porcentajeMensualGeneralMitcen)
        : null;
      const chartValue = this.chartPayload && Number.isFinite(Number(this.chartPayload.porcentajeMensualGeneralMitcen))
        ? Number(this.chartPayload.porcentajeMensualGeneralMitcen)
        : null;
      return {
        porcentajeMensualGeneralMitcen: tableValue != null ? tableValue : (chartValue != null ? chartValue : 0),
      };
    },
    period() {
      return this.tablePayload && this.tablePayload.periodo ? this.tablePayload.periodo : {};
    },
    reportTitle() {
      return this.period.titulo || this.chartPayload.titulo || "Cumplimiento del Plan de Producción";
    },
    chartTitle() {
      return "PORCENTAJE MENSUAL GENERAL MITCEN";
    },
    chartSeries() {
      const series = Array.isArray(this.chartPayload && this.chartPayload.series) ? this.chartPayload.series : [];
      return series.map((item) => ({
        name: item && item.nombre ? item.nombre : "Serie",
        data: Array.isArray(item && item.data) ? item.data.map((value) => Number(value) || 0) : [],
      }));
    },
    chartCategories() {
      return Array.isArray(this.chartPayload && this.chartPayload.categorias) ? this.chartPayload.categorias : [];
    },
    hasChartData() {
      return this.chartSeries.some((serie) => Array.isArray(serie.data) && serie.data.length > 0);
    },
    weeklyChartCategories() {
      return this.rows.map((row) => row.operadorMaquina || `${row.operador || ""} (${row.maquina || ""})`.trim());
    },
    weeklyChartSeries() {
      const weekDefs = [
        { key: "semana01", name: "Semana 01" },
        { key: "semana02", name: "Semana 02" },
        { key: "semana03", name: "Semana 03" },
        { key: "semana04", name: "Semana 04" },
        { key: "semana05", name: "Semana 05" },
      ];

      return weekDefs
        .map((week) => ({
          name: week.name,
          data: this.rows.map((row) => this.toNumeric(row[week.key])),
        }))
        .filter((serie) => serie.data.some((value) => value > 0));
    },
    hasWeeklyBreakdownData() {
      return this.weeklyChartSeries.length > 0 && this.weeklyChartCategories.length > 0;
    },
    stopChartCategories() {
      return this.rows.map((row) => row.operadorMaquina || `${row.operador || ""} (${row.maquina || ""})`.trim());
    },
    stopChartSeries() {
      return [
        {
          name: "Porcentaje Paro Mensual",
          data: this.rows.map((row) => this.toNumeric(row.porcentajeParoMensual)),
        },
      ];
    },
    hasStopChartData() {
      return this.stopChartSeries[0].data.some((value) => value > 0);
    },
    chartHeight() {
      return Math.max(560, Math.ceil(this.chartCategories.length / 2) * 160);
    },
    weeklyChartHeight() {
      return Math.max(620, Math.ceil(this.weeklyChartCategories.length / 2) * 170);
    },
    stopChartHeight() {
      return Math.max(560, Math.ceil(this.stopChartCategories.length / 2) * 160);
    },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          animations: { enabled: true },
          parentHeightOffset: 24,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6,
            columnWidth: "50%",
          },
        },
        colors: ["#1e88e5"],
        dataLabels: {
          enabled: true,
          formatter: (value) => `${this.formatNumber(value)}%`,
          style: {
            fontSize: "11px",
            fontWeight: 600,
          },
        },
        xaxis: {
          categories: this.chartCategories,
          labels: {
            rotate: -35,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
            minHeight: 88,
            maxHeight: 110,
            formatter: (value) => this.wrapAxisLabel(value, 40),
            style: {
              fontSize: "8px",
            },
          },
        },
        yaxis: {
          min: 0,
          max: 100,
          labels: {
            formatter: (value) => `${this.formatNumber(value)}%`,
          },
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 4,
        },
        tooltip: {
          y: {
            formatter: (value) => `${this.formatNumber(value)}%`,
          },
        },
        legend: {
          show: this.chartSeries.length > 1,
        },
      };
    },
    weeklyChartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          animations: { enabled: true },
          parentHeightOffset: 24,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: "64%",
          },
        },
        colors: ["#5b9bd5", "#ed7d31", "#70ad47", "#ffc000", "#4472c4"],
        dataLabels: {
          enabled: true,
          formatter: (value) => (value > 0 ? this.formatNumber(value) : ""),
          style: {
            fontSize: "10px",
            fontWeight: 700,
            colors: ["#111827"],
          },
          offsetY: -4,
        },
        stroke: {
          show: true,
          width: 0,
        },
        xaxis: {
          categories: this.weeklyChartCategories,
          labels: {
            rotate: -35,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
            minHeight: 88,
            maxHeight: 110,
            formatter: (value) => this.wrapAxisLabel(value, 40),
            style: {
              fontSize: "8px",
            },
          },
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 4,
          labels: {
            formatter: (value) => `${this.formatNumber(value)}%`,
          },
          title: {
            text: "Porcentaje",
          },
        },
        grid: {
          borderColor: "#d1d5db",
          strokeDashArray: 2,
        },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "center",
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) => `${this.formatNumber(value)}%`,
          },
        },
      };
    },
    stopChartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          animations: { enabled: true },
          parentHeightOffset: 24,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: "48%",
          },
        },
        colors: ["#ef4444"],
        dataLabels: {
          enabled: true,
          formatter: (value) => `${this.formatNumber(value)}%`,
          style: {
            fontSize: "10px",
            fontWeight: 700,
            colors: ["#111827"],
          },
          offsetY: -4,
        },
        xaxis: {
          categories: this.stopChartCategories,
          labels: {
            rotate: -35,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
            minHeight: 88,
            maxHeight: 110,
            formatter: (value) => this.wrapAxisLabel(value, 40),
            style: {
              fontSize: "8px",
            },
          },
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 4,
          labels: {
            formatter: (value) => `${this.formatNumber(value)}%`,
          },
          title: {
            text: "Porcentaje",
          },
        },
        grid: {
          borderColor: "#d1d5db",
          strokeDashArray: 2,
        },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "center",
        },
        tooltip: {
          y: {
            formatter: (value) => `${this.formatNumber(value)}%`,
          },
        },
      };
    },
  },
  methods: {
    init() {
      return this.fetchReport();
    },
    buildReportUrl() {
      return "UnificadorPlanProduccion/ObtenerGraficaCumplimientoPlanProduccionMensual";
    },
    buildTableUrl() {
      return "UnificadorPlanProduccion/EficienciaOperadorMaquinaMensual";
    },
    buildMonthlyOperatorMachineEfficiencyExcelUrl() {
      const period = this.getSelectedPeriod();
      if (!period) return null;
      const params = new URLSearchParams({
        año: `${period.year}`,
        mes: `${period.month}`,
      });
      return `UnificadorPlanProduccion/ExportarEficienciaOperadorMaquinaMensualExcel?${params.toString()}`;
    },
    buildExportComplianceWithChartsUrl() {
      return "UnificadorPlanProduccion/ExportarCumplimientoPlanProduccionMensualConGraficas";
    },
    async fetchReport() {
      const year = Number(this.filters.year);
      const month = Number(this.filters.month);

      if (!Number.isInteger(year) || !Number.isInteger(month)) {
        this.errorMessage = "Selecciona un año y un mes válidos.";
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      try {
        const [tableResponse, chartResponse] = await Promise.all([
          axios.get(this.buildTableUrl(), {
            params: {
              año: year,
              mes: month,
            },
          }),
          axios.get(this.buildReportUrl(), {
            params: {
              año: year,
              mes: month,
            },
          }),
        ]);
        this.tablePayload = this.normalizeTablePayload(tableResponse && tableResponse.data);
        this.chartPayload = this.normalizeChartPayload(chartResponse && chartResponse.data);
      } catch (error) {
        this.tablePayload = this.normalizeTablePayload(null);
        this.chartPayload = this.normalizeChartPayload(null);
        this.errorMessage = "No se pudo cargar el cumplimiento del plan de producción.";
        console.error("No se pudo consultar el cumplimiento del plan de producción mensual", error);
      } finally {
        this.loading = false;
      }
    },
    async downloadMonthlyOperatorMachineEfficiencyExcel() {
      const period = this.getSelectedPeriod();
      const url = this.buildExportComplianceWithChartsUrl();
      if (!period || !url) {
        this.errorMessage = "Selecciona un año y un mes válidos.";
        return;
      }

      this.downloadingExcel = true;
      this.errorMessage = "";

      try {
        const payload = {
          Año: period.year,
          Mes: period.month,
          GraficaPorcentajeMensualGeneralBase64: await this.getChartBase64("generalChart"),
          GraficaDesgloseSemanalBase64: await this.getChartBase64("weeklyChart"),
          GraficaParoMensualBase64: await this.getChartBase64("stopChart"),
        };
        const response = await axios.post(url, payload, {
          responseType: "blob",
        });
        const fallbackFileName = `CumplimientoPlanProduccion_${period.year}_${String(period.month).padStart(2, "0")}.xlsx`;
        this.saveExcelResponse(response, fallbackFileName);
      } catch (error) {
        this.errorMessage = "No se pudo descargar el Excel de cumplimiento.";
        console.error("No se pudo exportar el cumplimiento del plan de produccion con graficas", error);
      } finally {
        this.downloadingExcel = false;
      }
    },
    normalizeTablePayload(data) {
      const fallback = emptyPayload();
      const payload = data && typeof data === "object" ? data : fallback;
      const period = payload.periodo && typeof payload.periodo === "object" ? payload.periodo : {};
      const monthValue = Number(period.mes || this.filters.month);
      const yearValue = Number(period.año || period.anio || this.filters.year);
      const rawRows = Array.isArray(data)
        ? data
        : Array.isArray(payload.filas)
          ? payload.filas
          : Array.isArray(payload.tabla)
            ? payload.tabla
            : Object.values(payload).filter((item) => item && typeof item === "object" && (
              Object.prototype.hasOwnProperty.call(item, "operadorMaquina")
              || Object.prototype.hasOwnProperty.call(item, "operador")
              || Object.prototype.hasOwnProperty.call(item, "maquina")
            ));

      return {
        periodo: {
          año: yearValue,
          mes: monthValue,
          mesNombre: period.mesNombre || this.resolveMonthName(monthValue),
          titulo: period.titulo || `Cumplimiento del Plan de Producción de ${this.resolveMonthName(monthValue)} ${yearValue}`,
        },
        filas: rawRows.map((row, index) => {
          const porcentajeMensual = this.toNumeric(row && row.porcentajeMensual);
          const porcentajeParoMensual = this.toNumeric(row && row.porcentajeParoMensual);
          return {
            id: `${index}-${row && row.operadorMaquina ? row.operadorMaquina : "row"}`,
            operador: row && row.operador ? row.operador : "",
            maquina: row && row.maquina ? row.maquina : "",
            operadorMaquina: row && row.operadorMaquina ? row.operadorMaquina : "",
            semana01: this.toNumeric(row && row.semana01),
            semana02: this.toNumeric(row && row.semana02),
            semana03: this.toNumeric(row && row.semana03),
            semana04: this.toNumeric(row && row.semana04),
            semana05: this.toNumeric(row && row.semana05),
            sumaTotalMensual: this.toNumeric(row && row.sumaTotalMensual),
            porcentajeMensual,
            porcentajeParoMensual,
            totalMensual: porcentajeMensual + porcentajeParoMensual,
          };
        }),
        resumen: {
          porcentajeMensualGeneralMitcen: payload.resumen
            && payload.resumen.porcentajeMensualGeneralMitcen != null
            && payload.resumen.porcentajeMensualGeneralMitcen !== ""
            && Number.isFinite(Number(payload.resumen.porcentajeMensualGeneralMitcen))
            ? Number(payload.resumen.porcentajeMensualGeneralMitcen)
            : null,
        },
      };
    },
    normalizeChartPayload(data) {
      const fallback = emptyChartPayload();
      const payload = data && typeof data === "object" ? data : fallback;
      const monthValue = Number(payload.mes || this.filters.month);
      const yearValue = Number(payload.año || payload.anio || this.filters.year);

      return {
        año: yearValue,
        mes: monthValue,
        mesNombre: payload.mesNombre || this.resolveMonthName(monthValue),
        titulo: payload.titulo || fallback.titulo,
        categorias: Array.isArray(payload.categorias) ? payload.categorias : [],
        series: Array.isArray(payload.series) ? payload.series : [],
        porcentajeMensualGeneralMitcen: this.toNumeric(payload.porcentajeMensualGeneralMitcen),
      };
    },
    resolveMonthName(month) {
      const match = this.monthOptions.find((item) => item.value === Number(month));
      return match ? match.text : "";
    },
    getSelectedPeriod() {
      const year = Number(this.filters.year);
      const month = Number(this.filters.month);
      if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
        return null;
      }
      return { year, month };
    },
    toNumeric(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    formatNumber(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return "0.00";
      return number.toFixed(2);
    },
    formatPercent(value) {
      return `${this.formatNumber(value)}%`;
    },
    wrapAxisLabel(value, chunkSize = 22) {
      const text = String(value || "").replace(/\s+/g, " ").trim();
      if (!text) return "";
      const normalized = text.replace(/\s*-\s*/g, " -\n").replace(/\s*\(\s*/g, "\n(");
      const parts = normalized.split("\n");
      const wrapped = parts
        .flatMap((part) => {
          if (part.length <= chunkSize) return [part];
          const words = part.split(" ");
          const lines = [];
          let current = "";
          words.forEach((word) => {
            const next = current ? `${current} ${word}` : word;
            if (next.length <= chunkSize) {
              current = next;
              return;
            }
            if (current) lines.push(current);
            current = word;
          });
          if (current) lines.push(current);
          return lines;
        })
        .filter((line) => line);
      return wrapped.join("\n");
    },
    async getChartBase64(refName) {
      await this.$nextTick();
      const chartRef = this.$refs[refName];
      if (!chartRef || typeof chartRef.dataURI !== "function") return "";
      const result = await chartRef.dataURI({ scale: 3 });
      const imageUri = result && result.imgURI ? result.imgURI : "";
      return this.extractBase64Content(imageUri);
    },
    extractBase64Content(dataUri) {
      if (!dataUri || typeof dataUri !== "string") return "";
      const base64Marker = "base64,";
      const markerIndex = dataUri.indexOf(base64Marker);
      if (markerIndex === -1) return dataUri;
      return dataUri.slice(markerIndex + base64Marker.length);
    },
    saveExcelResponse(response, fallbackFileName) {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response && response.headers
        ? response.headers["content-disposition"] || response.headers["Content-Disposition"]
        : "";
      const fileNameMatch = typeof disposition === "string"
        ? disposition.match(/filename\*?=(?:UTF-8''|\"?)([^\";]+)/i)
        : null;
      const fileName = fileNameMatch && fileNameMatch[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, "").trim())
        : fallbackFileName;
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
  mounted() {
    this.fetchReport();
  },
};
</script>

<style scoped>
.cumplimiento-card {
  border: 1px solid #e0e6ef;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.cumplimiento-header {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafbfe;
}

.cumplimiento-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.cumplimiento-filter {
  min-width: 180px;
  max-width: 220px;
}

.cumplimiento-title-block {
  margin-top: 14px;
}

.cumplimiento-title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.cumplimiento-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
}

.cumplimiento-body {
  padding: 12px;
}

.cumplimiento-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.summary-card {
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  background: linear-gradient(180deg, #f9fbff 0%, #eef4ff 100%);
  padding: 16px;
}

.summary-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.summary-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.cumplimiento-panel {
  margin-bottom: 12px;
  border-radius: 10px;
}

.panel-title {
  padding: 14px 16px 0;
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
}

.chart-wrap {
  padding: 8px 12px 12px;
}

.empty-state {
  padding: 24px 16px;
  color: #6b7280;
  text-align: center;
}

.cumplimiento-table {
  padding: 8px 12px 12px;
}
</style>
